import React, {FC, useRef, useState} from "react"
import {Alert, Badge, Button, Card, Col, Container, Form, Row} from "react-bootstrap"

import MLeft from './m_left.png'
import MRight from './m_right.png'
import Azerty from './kbl_azerty.png'
import Qwerty from './kbl_qwerty.png'
import Qwertz from './kbl_qwertz.png'
import 'moment/dist/locale/de'
import axios from "axios"
import './BookingComponent.scss'
import {Trans, useTranslation} from "react-i18next"
import {useNavigate, useParams} from "react-router-dom"
import {useAuth} from "../../context/AuthProvider"
import FullCalendar from "@fullcalendar/react"
import timeGridWeek from "@fullcalendar/timegrid"
import bootstrap5Plugin from "@fullcalendar/bootstrap5"
import momentPlugin from '@fullcalendar/moment'
import momentTimezonePlugin from '@fullcalendar/moment-timezone'

import moment from "moment-timezone"

type BookingComponentProps = Record<string, unknown>

type BookingDayUnavailableReason =
    | "SITE_CLOSED"
    | "PUBLIC_HOLIDAY"
    | "UNIVERSITY_CLOSED"
    | "BOOKING_OFFSET"
    | "NO_AVAILABLE_OPTION"

type BookingDayAvailability = {
    day: string
    available: boolean
    reason?: BookingDayUnavailableReason
    reasonDetails?: string
    optionCount?: number
}

type BookingAvailableDaysResponse = {
    siteDays: number[]
    days: BookingDayAvailability[]
}

type AvailableBookingOption = {
    type: "new" | "existing"
    bookingSlotId?: string
    entryAt: string
    startAt: string
    endAt: string
    leaveUntil: string
    load: number
    requiresExtension: boolean
}

type BookingContext = {
    handicap: number
    exclusiveBooking: boolean
    handicapNotes: string
    disabilityOfficerEmails: string[]
}

const SITE_ID = "6784d8aeb2e2dd156850e00a"

const BookingComponent: FC<BookingComponentProps> = () => {
    const {t, i18n} = useTranslation("booking")
    const nav = useNavigate()
    const auth = useAuth()
    const params = useParams()

    const [selectedExam, setSelectedExam] = useState<any>(null)
    const [preferredExamConfig, setPreferredExamConfig] = useState<any>(null)
    const [bookingContext, setBookingContext] = useState<BookingContext | null>(null)
    const [possibleSlots, setPossibleSlots] = useState<AvailableBookingOption[]>([])
    const [view, setView] = useState<number>(0)
    const [selectedTimeSlot, setSelectedTimeSlot] = useState<AvailableBookingOption | null>(null)
    const [currentCalendarDate, setCurrentCalendarDate] = useState<number>(Date.now())
    const [availableDays, setAvailableDays] = useState<BookingDayAvailability[]>([])
    const [siteDays, setSiteDays] = useState<number[]>([])
    const [selectedDayStatus, setSelectedDayStatus] = useState<BookingDayAvailability | null>(null)
    const calendarRef = useRef<FullCalendar>(null)
    const [infoChecked, setInfoChecked] = useState(false)
    const [policyChecked, setPolicyChecked] = useState(false)
    const [tucanNoteChecked, setTucanNoteChecked] = useState(false)

    const requestAvailableDays = async (): Promise<void> => {
        const response = await axios.get<BookingDayAvailability[]>(
            `/api/booking/available-days/${params.id}` +
            `/6784d8aeb2e2dd156850e00a` +
            `/${preferredExamConfig.preferredKeyboardLayout}` +
            `/${preferredExamConfig.prefersMouseLeft}`,
            auth.axiosAuthHeader()
        )

        setAvailableDays(response.data)

        const firstAvailableDay = response.data.find(
            day => day.available
        )

        if (!firstAvailableDay) {
            setSelectedDayStatus(null)
            return
        }

        setSelectedDayStatus(firstAvailableDay)
        setCurrentCalendarDate(
            moment
                .tz(
                    firstAvailableDay.day,
                    "YYYY-MM-DD",
                    "Europe/Berlin"
                )
                .valueOf()
        )
    }

    React.useEffect(() => {
        axios.get("/api/user/profile/preferredExamConfig", auth.axiosAuthHeader()).then((response) => {
            setPreferredExamConfig(response.data)
        })

        axios.get("/api/exam/exam/" + params.id, auth.axiosAuthHeader()).then((response) => {
            setSelectedExam(response.data)
        })

        axios.get(`/api/exam/booking/context/${params.id}`, auth.axiosAuthHeader()).then((response) => {
            setBookingContext(response.data)
        })

    }, [])

    const requestAvailableSlots = async (date: number): Promise<void> => {
        const response = await axios.get<AvailableBookingOption[]>(
            `/api/booking/available/${params.id}` +
            `/${SITE_ID}` +
            `/${date}` +
            `/${preferredExamConfig.preferredKeyboardLayout}` +
            `/${preferredExamConfig.prefersMouseLeft}`,
            auth.axiosAuthHeader()
        )

        setPossibleSlots(response.data)
        setSelectedTimeSlot(null)
    }

    React.useEffect(() => {
        if (view !== 1 || !selectedDayStatus) {
            return
        }

        if (!selectedDayStatus.available) {
            setPossibleSlots(previous =>
                previous.length === 0
                    ? previous
                    : []
            )

            setSelectedTimeSlot(null)
            return
        }

        requestAvailableSlots(currentCalendarDate).then()
    }, [
        view,
        currentCalendarDate,
        selectedDayStatus?.day,
        selectedDayStatus?.available,
    ])

    const getUnavailableDayMessage = (
        status: BookingDayAvailability
    ): string => {
        switch (status.reason) {
            case "SITE_CLOSED":
                return t(
                    "booking.dayUnavailable.siteClosed"
                )

            case "PUBLIC_HOLIDAY":
                return t(
                    "booking.dayUnavailable.publicHoliday",
                    {
                        name:
                            status.reasonDetails ??
                            "",
                    }
                )

            case "UNIVERSITY_CLOSED":
                return t(
                    "booking.dayUnavailable.universityClosed",
                    {
                        name:
                            status.reasonDetails ??
                            "",
                    }
                )

            case "BOOKING_OFFSET":
                return t(
                    "booking.dayUnavailable.bookingOffset"
                )

            default:
                return t(
                    "booking.dayUnavailable.noOption"
                )
        }
    }

    function renderEventContent(eventInfo: any) {
        const option =
            eventInfo.event.extendedProps.option as AvailableBookingOption

        return (
            <span>
            {eventInfo.timeText}{" "}

                {option.load === 0 ? (
                    <Badge bg="info">
                        <i className="bi bi-thermometer-low" />{" "}
                        {t("booking.step2.util.1")}
                    </Badge>
                ) : null}

                {option.load === 1 ? (
                    <Badge bg="primary">
                        <i className="bi bi-thermometer-low" />{" "}
                        {t("booking.step2.util.2")}
                    </Badge>
                ) : null}

                {option.load === 2 ? (
                    <Badge bg="warning">
                        <i className="bi bi-thermometer-half" />{" "}
                        {t("booking.step2.util.3")}
                    </Badge>
                ) : null}

                {option.load === 3 ? (
                    <Badge bg="danger">
                        <i className="bi bi-thermometer-high" />{" "}
                        {t("booking.step2.util.4")}
                    </Badge>
                ) : null}

                {option.requiresExtension ? (
                    <>
                        <br />
                        <Badge bg="warning">
                            <i className="bi bi-plus-slash-minus" />{" "}
                            {t("book.field.badge.2")}
                        </Badge>
                    </>
                ) : null}
        </span>
        )
    }

    const handleEventClick = (eventInfo: any): void => {
        const option =
            eventInfo.event.extendedProps.option as AvailableBookingOption

        setSelectedTimeSlot(option)
    }
    const changeDate = (dateInfo: any): void => {
        const day = moment
            .tz(dateInfo.start, "Europe/Berlin")
            .format("YYYY-MM-DD")

        const status =
            availableDays.find(
                availableDay => availableDay.day === day
            ) ?? null

        const nextCalendarDate = dateInfo.start.getTime()

        setSelectedDayStatus(previous =>
            previous?.day === status?.day &&
            previous?.available === status?.available &&
            previous?.reason === status?.reason
                ? previous
                : status
        )

        setCurrentCalendarDate(previous =>
            previous === nextCalendarDate
                ? previous
                : nextCalendarDate
        )
    }
    const gotoView = async (
        viewNumber: number
    ): Promise<void> => {
        await axios.post(
            "/api/user/profile/preferredExamConfig/" +
            preferredExamConfig.preferredKeyboardLayout +
            "/" +
            preferredExamConfig.prefersMouseLeft,
            null,
            auth.axiosAuthHeader()
        )

        if (viewNumber === 1) {
            await requestAvailableDays()
        }

        setView(viewNumber)
    }
    const confirmBooking = async (): Promise<void> => {
        if (!selectedTimeSlot || !selectedExam || !preferredExamConfig) {
            return
        }

        try {
            await axios.post(
                "/api/exam/booking/submit",
                {
                    examId: selectedExam._id,
                    siteId: SITE_ID,
                    option: {
                        type: selectedTimeSlot.type,
                        bookingSlotId: selectedTimeSlot.bookingSlotId,
                        entryAt: selectedTimeSlot.entryAt,
                    },
                    preferences: {
                        keyboardLayout: preferredExamConfig.preferredKeyboardLayout,
                        mouseLeft: preferredExamConfig.prefersMouseLeft,
                        avoidWindow: preferredExamConfig.avoidWindow === true,
                    },
                },
                auth.axiosAuthHeader()
            )

            nav("/booking")
        } catch (error: any) {
            if (
                error.response?.status === 409 ||
                error.response?.data?.code === "OPTION_NO_LONGER_AVAILABLE"
            ) {
                alert(t("booking.messages.slotUnavailable"))
                setSelectedTimeSlot(null)
                setView(1)
                await requestAvailableDays()
                return
            }

            console.error(error)
            alert(t("booking.messages.failed"))
        }
    }
    const previousDay = (): void => {
        calendarRef.current?.getApi().prev()
    }

    const nextDay = (): void => {
        calendarRef.current?.getApi().next()
    }

    const formattedCalendarDate = moment(currentCalendarDate.valueOf())
        .locale(i18n.language)
        .format("dddd, D. MMMM YYYY")

    const bookingSteps = [
        t("booking.flow.settings"),
        t("booking.flow.appointment"),
        t("booking.flow.confirmation"),
    ]

    return (<div className="component booking-component">
            <Container fluid className="booking-container">
                <Card className="booking-header mb-4">
                    <Card.Body>
                        <div className="d-flex flex-column flex-md-row justify-content-between gap-3 align-items-md-start">
                            <div>
                                <h1 className="h2 mb-1">{t("booking.flow.title")}</h1>
                                {selectedExam ? (
                                    <p className="text-muted mb-0">
                                        {i18n.language === "en"
                                            ? selectedExam.name_en || selectedExam.name_de
                                            : selectedExam.name_de}
                                    </p>
                                ) : null}
                            </div>
                            <Badge bg="primary" pill className="booking-progress-badge">
                                {t("booking.flow.progress", {current: view + 1, total: bookingSteps.length})}
                            </Badge>
                        </div>
                        <ol className="booking-stepper mt-4 mb-0" aria-label={t("booking.flow.progressLabel")}>
                            {bookingSteps.map((step, index) => (
                                <li
                                    key={step}
                                    className={`${index === view ? "is-active" : ""} ${index < view ? "is-complete" : ""}`}
                                    aria-current={index === view ? "step" : undefined}
                                >
                                    <span className="booking-step-number" aria-hidden="true">
                                        {index < view ? <i className="bi bi-check-lg"/> : index + 1}
                                    </span>
                                    <span>{step}</span>
                                </li>
                            ))}
                        </ol>
                    </Card.Body>
                </Card>
                {view === 0 ? <><Row>
                    <Col>
                        <Card className="booking-panel">
                            <Card.Header><h2 className="h4 mb-0"><i className="bi bi-sliders me-2"/>{t("booking.step1.title")}</h2></Card.Header>
                            <Card.Body>
                                {selectedExam && !selectedExam.isDemo && bookingContext && (() => {
                                    const hasAccommodation =
                                        bookingContext.handicap > 0 ||
                                        bookingContext.exclusiveBooking ||
                                        Boolean(bookingContext.handicapNotes?.trim())
                                    const recipients = bookingContext.disabilityOfficerEmails.join(",")
                                    const mailto = recipients
                                        ? `mailto:${recipients}?subject=${encodeURIComponent(
                                            t("booking.step1.handicap.mailSubject", {
                                                exam: i18n.language === "en"
                                                    ? selectedExam.name_en || selectedExam.name_de
                                                    : selectedExam.name_de,
                                            })
                                        )}`
                                        : null

                                    return hasAccommodation ? (
                                        <Alert variant="success">
                                            <Alert.Heading>
                                                <i className="bi bi-universal-access me-2"/>
                                                {t("booking.step1.handicap.approvedTitle")}
                                            </Alert.Heading>
                                            <p>{t("booking.step1.handicap.approvedText")}</p>
                                            <ul className="mb-0">
                                                {bookingContext.handicap > 0 && (
                                                    <li>{t("booking.step1.handicap.extension", {
                                                        count: bookingContext.handicap,
                                                    })}</li>
                                                )}
                                                {bookingContext.exclusiveBooking && (
                                                    <li>{t("booking.step1.handicap.exclusive")}</li>
                                                )}
                                                {bookingContext.handicapNotes?.trim() && (
                                                    <li>{t("booking.step1.handicap.notes", {
                                                        notes: bookingContext.handicapNotes,
                                                    })}</li>
                                                )}
                                            </ul>
                                        </Alert>
                                    ) : (
                                        <Alert variant="info">
                                            <Alert.Heading>
                                                <i className="bi bi-universal-access me-2"/>
                                                {t("booking.step1.handicap.missingTitle")}
                                            </Alert.Heading>
                                            <p>{t("booking.step1.handicap.missingText")}</p>
                                            <p className="fw-semibold">
                                                <i className="bi bi-exclamation-triangle-fill me-2" aria-hidden="true"/>
                                                {t("booking.step1.handicap.waiverWarning")}
                                            </p>
                                            {mailto ? (
                                                <p className="mb-0">
                                                    {t("booking.step1.handicap.contact")}{" "}
                                                    <a href={mailto}>
                                                        {bookingContext.disabilityOfficerEmails.join(", ")}
                                                    </a>
                                                </p>
                                            ) : (
                                                <p className="mb-0">{t("booking.step1.handicap.noContact")}</p>
                                            )}
                                        </Alert>
                                    )
                                })()}
                                <Alert variant="info">
                                    <div className="d-flex align-items-start gap-2">
                                        <i className="bi bi-info-circle-fill fs-5" aria-hidden="true"/>
                                        <div>
                                            <p>{t('booking.step1.info1')}</p>
                                            <p className="mb-0">{t('booking.step1.info2')} <b>{t('booking.step1.info3')}</b></p>
                                        </div>
                                    </div>
                                </Alert>
                                <Row className="g-3">
                                    <Col>
                                        <Card>
                                            <Card.Header><h3 className="h5 mb-0">{t('booking.step1.mousePosition')}</h3></Card.Header>
                                            <Card.Body>
                                                {preferredExamConfig ? <Row className="g-3" role="radiogroup">
                                                    <Col xs={12} md={6}>
                                                        <Card
                                                              onClick={() => setPreferredExamConfig(((previous: any) => ({
                                                                  ...previous,
                                                                  prefersMouseLeft: true
                                                              })))}
                                                              role="radio" tabIndex={0}
                                                              aria-checked={preferredExamConfig.prefersMouseLeft}
                                                              onKeyDown={event => (event.key === "Enter" || event.key === " ") && setPreferredExamConfig((previous: any) => ({...previous, prefersMouseLeft: true}))}
                                                              className={`booking-choice ${preferredExamConfig.prefersMouseLeft ? "is-selected" : ""}`}>
                                                            <Card.Body>
                                                                <h4>{t('booking.step1.mousePosition.left')}</h4>
                                                                <img src={MLeft} className="booking-choice-image" alt="mouseLeft"></img></Card.Body></Card></Col>
                                                    <Col xs={12} md={6}><Card
                                                               onClick={() => setPreferredExamConfig(((previous: any) => ({
                                                                   ...previous,
                                                                   prefersMouseLeft: false
                                                               })))}
                                                               role="radio" tabIndex={0}
                                                               aria-checked={!preferredExamConfig.prefersMouseLeft}
                                                               onKeyDown={event => (event.key === "Enter" || event.key === " ") && setPreferredExamConfig((previous: any) => ({...previous, prefersMouseLeft: false}))}
                                                               className={`booking-choice ${!preferredExamConfig.prefersMouseLeft ? "is-selected" : ""}`}><Card.Body>
                                                        <h4>{t('booking.step1.mousePosition.right')}</h4>
                                                        <img src={MRight} className="booking-choice-image" alt="mouseRight"></img></Card.Body></Card></Col></Row> : null}
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                                <Row className="mt-3">
                                    <Col>
                                        <Card>
                                            <Card.Header><h3 className="h5 mb-0">{t('booking.step1.keyboardLayout')}</h3></Card.Header>
                                            <Card.Body>
                                                {preferredExamConfig ? <Row className="g-3" role="radiogroup">
                                                    <Col xs={12} md={4}><Card
                                                               onClick={() => setPreferredExamConfig(((previous: any) => ({
                                                                   ...previous,
                                                                   preferredKeyboardLayout: "QWERTZ"
                                                               })))}
                                                               className={`booking-choice ${preferredExamConfig.preferredKeyboardLayout === "QWERTZ" ? "is-selected" : ""}`}><Card.Body>
                                                        <h4>{t('booking.step1.keyboardLayout.qwertz')}</h4><img
                                                        src={Qwertz}
                                                        className="booking-choice-image" alt="qwertz"/></Card.Body></Card></Col>
                                                    <Col xs={12} md={4}><Card
                                                               onClick={() => setPreferredExamConfig(((previous: any) => ({
                                                                   ...previous,
                                                                   preferredKeyboardLayout: "QWERTY"
                                                               })))}
                                                               className={`booking-choice ${preferredExamConfig.preferredKeyboardLayout === "QWERTY" ? "is-selected" : ""}`}><Card.Body>
                                                        <h4>{t('booking.step1.keyboardLayout.qwerty')}</h4><img
                                                        src={Qwerty}
                                                        className="booking-choice-image" alt="qwerty"/></Card.Body></Card></Col>
                                                    <Col xs={12} md={4}><Card
                                                               onClick={() => setPreferredExamConfig(((previous: any) => ({
                                                                   ...previous,
                                                                   preferredKeyboardLayout: "AZERTY"
                                                               })))}
                                                               className={`booking-choice ${preferredExamConfig.preferredKeyboardLayout === "AZERTY" ? "is-selected" : ""}`}><Card.Body>
                                                        <h4>{t('booking.step1.keyboardLayout.azerty')}</h4><img
                                                        src={Azerty}
                                                        className="booking-choice-image" alt="azerty"/></Card.Body></Card></Col>
                                                </Row> : null}
                                                <Row className={"mt-3 d-flex justify-content-end"}>
                                                    <Col xs={12} lg={8}><Alert className="mb-0 d-flex align-items-start gap-2"
                                                        variant="info"><i className="bi bi-keyboard-fill" aria-hidden="true"/>
                                                        <span>{t('booking.step1.keyboardLayout.hint')}</span></Alert></Col>
                                                </Row>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                                <Row className="booking-actions g-2">
                                    <Col className="m-2">
                                        <Button variant="outline-secondary" onClick={() => nav("/booking")}><i
                                            className="bi bi-caret-left-square-fill"></i> {t('booking.step1.button.cancel')}
                                        </Button>
                                    </Col>
                                    <Col className="mt-3 d-flex justify-content-end">
                                        <Button onClick={() => gotoView(1)}>
                                            {t('booking.step1.button.next')} <i
                                            className="bi bi-caret-right-square-fill"></i>
                                        </Button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                </> : view === 1 ? <Row>
                    <Col>
                        <Card className="booking-panel">
                            <Card.Header><h2 className="h4 mb-0"><i className="bi bi-calendar3 me-2"/>{t('booking.step2.title')}</h2></Card.Header>
                            <Card.Body>
                                <Alert variant="info" className="d-flex align-items-start gap-2">
                                    <i className="bi bi-info-circle-fill fs-5" aria-hidden="true"/>
                                    <span>{t('booking.step2.info1')}</span>
                                </Alert>
                                {availableDays.length > 0 &&
                                !availableDays.some(day => day.available) ? (
                                    <Alert variant="warning">
                                        <i className="bi bi-calendar-x-fill me-2" aria-hidden="true"/>
                                        {t("booking.dayUnavailable.noOption")}
                                    </Alert>
                                ) : null}
                                <Row className="align-items-center mb-3">
                                    <Col xs={12} md={4}>
                                        <Button
                                            variant="outline-primary"
                                            onClick={previousDay}
                                        >
                                            <i className="bi bi-arrow-left me-2" />
                                            {t("booking.calendar.previousDay")}
                                        </Button>
                                    </Col>

                                    <Col
                                        xs={12}
                                        md={4}
                                        className="text-center my-3 my-md-0"
                                    >
                                        <h3 className="mb-0">
                                            <i className="bi bi-calendar3 me-2" />
                                            {formattedCalendarDate}
                                        </h3>
                                    </Col>

                                    <Col
                                        xs={12}
                                        md={4}
                                        className="text-md-end"
                                    >
                                        <Button
                                            variant="outline-primary"
                                            onClick={nextDay}
                                        >
                                            {t("booking.calendar.nextDay")}
                                            <i className="bi bi-arrow-right ms-2" />
                                        </Button>
                                    </Col>
                                </Row>
                                {selectedDayStatus &&
                                !selectedDayStatus.available ? (
                                    <Alert variant="warning">
                                        <i className="bi bi-calendar-x-fill me-2" aria-hidden="true"/>
                                        {getUnavailableDayMessage(
                                            selectedDayStatus
                                        )}
                                    </Alert>
                                ) : null}
                                {selectedExam && availableDays.length > 0 ? <FullCalendar
                                    ref={calendarRef}
                                    plugins={[momentTimezonePlugin, momentPlugin, timeGridWeek, bootstrap5Plugin]}
                                    initialView='timeGridDay'
                                    initialDate={new Date(currentCalendarDate)}
                                    datesSet={(d: any) => changeDate(d)}
                                    headerToolbar={false}
                                    validRange={{
                                        start: moment.max(
                                            moment(selectedExam.openExaminationRange),
                                            moment().startOf('day')
                                        ).format('YYYY-MM-DD'),
                                        end: moment(selectedExam.closeExaminationRange)
                                            .add(1, 'day')
                                            .format('YYYY-MM-DD')
                                    }}
                                    timeZone="Europe/Berlin"
                                    slotLabelFormat="HH:mm"
                                    eventTimeFormat="HH:mm"
                                    titleFormat='MMMM D, YYYY' // you can now use moment format strings!
                                    slotMinTime="06:00"
                                    slotMaxTime="22:00"
                                    allDaySlot={false}
                                    selectable={true}
                                    eventClick={handleEventClick}
                                    themeSystem='bootstrap5'
                                    weekends={false}
                                    eventClassNames={eventInfo => {
                                        const option =
                                            eventInfo.event.extendedProps.option as AvailableBookingOption

                                        const selected =
                                            selectedTimeSlot?.type === option.type &&
                                            selectedTimeSlot?.bookingSlotId === option.bookingSlotId &&
                                            new Date(selectedTimeSlot?.entryAt ?? 0).getTime() ===
                                            new Date(option.entryAt).getTime()

                                        return selected
                                            ? ["bg-success", "border-success"]
                                            : []
                                    }}
                                    events={possibleSlots.map((slot: AvailableBookingOption) => ({
                                        id: slot.bookingSlotId ?? `new-${slot.entryAt}`,
                                        start: slot.entryAt,
                                        end: slot.leaveUntil,
                                        title: String(slot.load),
                                        extendedProps: {
                                            option: slot,
                                        },
                                    }))}
                                    eventContent={renderEventContent}
                                /> : null}
                                <Row className="booking-actions g-2">
                                    <Col className="m-2">
                                        <Button variant="outline-secondary" onClick={() => {
                                            setSelectedTimeSlot(null)
                                            setView(0)
                                        }}><i
                                            className="bi bi-caret-left-square-fill"></i> {t('booking.step1.button.previous')}
                                        </Button>
                                    </Col>
                                    <Col className="mt-3 d-flex justify-content-end">
                                        <Button disabled={selectedTimeSlot == null}
                                                onClick={() => setView(2)}>{t('booking.step1.button.next')} <i
                                            className="bi bi-caret-right-square-fill"></i>
                                        </Button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row> : selectedTimeSlot ? (<Row>
                    <Col>
                        <Card className="booking-panel">
                            <Card.Header><h2 className="h4 mb-0"><i className="bi bi-check2-circle me-2"/>{t("booking.step3.title")}</h2></Card.Header>
                            <Card.Body>
                                <Row><Col>
                                    <Card><Card.Body>
                                        <h3>{t('booking.step3.info1')}: {selectedExam.name_de}</h3>
                                        <hr/>
                                        <Row>
                                            <Col>
                                                <h4>{t('booking.step3.info2')}</h4>
                                                <ul>
                                                    <li><Trans ns="booking" i18nKey="booking.step3.schedule.date"
                                                               values={{date: moment(selectedTimeSlot.startAt).format("DD.MM.YYYY")}}
                                                               components={{strong: <strong/>}}/></li>
                                                    <li>
                                                        <Trans ns="booking" i18nKey="booking.step3.schedule.entry" values={{time: moment(selectedTimeSlot.entryAt).format("HH:mm")}} components={{strong: <strong/>}}/>
                                                    </li>
                                                    <li>
                                                        <Trans ns="booking" i18nKey="booking.step3.schedule.start" values={{time: moment(selectedTimeSlot.startAt).format("HH:mm")}} components={{strong: <strong/>}}/>
                                                    </li>
                                                    <li>
                                                        <Trans ns="booking" i18nKey="booking.step3.schedule.end" values={{time: moment(selectedTimeSlot.startAt).add(selectedExam.duration, "minutes").format("HH:mm"), duration: selectedExam.duration}} components={{strong: <strong/>}}/>
                                                    </li>
                                                    <li><Trans ns="booking" i18nKey="booking.step3.schedule.leave" values={{time: moment(selectedTimeSlot.leaveUntil).format("HH:mm")}} components={{strong: <strong/>}}/></li>
                                                    <li><Trans ns="booking" i18nKey="booking.step3.schedule.restroom" components={{strong: <strong/>}}/></li>
                                                </ul>
                                                <h4>{t('booking.step3.info11')}</h4>
                                                <ul>
                                                    <li>{t('booking.step3.info12')}</li>
                                                    <li>{t('booking.step3.info13')}</li>
                                                    <li>{t('booking.step3.info14')}</li>
                                                    <li>{t('booking.step3.info15')}</li>
                                                </ul>
                                            </Col>
                                            <Col>
                                                <h4>{t('booking.step3.info16')}</h4>
                                                <ul>
                                                    <li>{t('booking.step3.info17')}
                                                        <ul>
                                                            <li className="text-muted">{t('booking.step3.info18')}</li>
                                                            <li className="text-muted">{t('booking.step3.info19')}</li>
                                                            <li className="text-muted">{t('booking.step3.info20')}</li>
                                                            <li className="text-muted">{t('booking.step3.info21')}</li>
                                                            <li className="text-muted">{t('booking.step3.info22')}</li>
                                                        </ul>
                                                    </li>
                                        <li>{t('booking.step3.info23')}</li>
                                        <li>{t('booking.step3.info23a')}</li>
                                        <li>{t('booking.step3.info24')}</li>
                                                </ul>
                                                <h4>{t('booking.step3.info25')}</h4>
                                                <ul>
                                                    <li>{t('booking.step3.info26')}</li>
                                                </ul>
                                                <h4>{t('booking.step3.info27')}</h4>
                                                <ul>
                                                    <li>{t('booking.step3.info28')}{" "}<b>{preferredExamConfig.prefersMouseLeft ? t('user.settings.value.prefersMouseRight.left', {ns: 'settings'}) : t('user.settings.value.prefersMouseRight.right', {ns: 'settings'})}</b>,
                                                    </li>
                                                    <li>{t('booking.step3.info29')}{" "}<b>{t('booking.step1.keyboardLayout.qwertz')}</b>,
                                                    </li>
                                                    {preferredExamConfig.preferredKeyboardLayout === "QWERTY" ?
                                                        <li>{t('booking.step3.info29')} <b>{t('booking.step1.keyboardLayout.qwerty')}</b>,
                                                        </li> : null}
                                                    {preferredExamConfig.preferredKeyboardLayout === "AZERTY" ?
                                                        <li>{t('booking.step3.info29')}{" "}<b>{t('booking.step1.keyboardLayout.azerty')}</b>,
                                                        </li> : null}
                                                    <li>{t('booking.step3.info30')}</li>
                                                </ul>
                                            </Col>
                                        </Row>
                                        <hr/>
                                        <Form>
                                            <h3 className={"d-flex justify-content-center " + (infoChecked? "text-success" : "text-warning")}>
                                                <Form.Check
                                                    type="checkbox"
                                                    id="custom-switch1"
                                                    label={t("booking.step3.info31")}
                                                    checked={infoChecked}
                                                    onChange={(event) =>
                                                        setInfoChecked(event.currentTarget.checked)
                                                    }
                                                /></h3>
                                        </Form>
                                    </Card.Body>
                                    </Card>

                                </Col></Row>
                                <Row className="mt-4">
                                    <Col>
                                        <Card><Card.Header><h2>{t('booking.step3.policy1')}</h2>
                                        </Card.Header>
                                            <Card.Body>
                                                <Row>
                                                    <Col>
                                                        <h4>{t('booking.step3.policy2')}</h4>
                                                        <p><Trans ns="booking" i18nKey="booking.step3.policy.monitoring" components={{strong: <strong/>}}/></p>
                                                        {selectedExam.isDemo ?
                                                            <p>{t('booking.step3.policy6')}</p> :
                                                            <p>{t('booking.step3.policy7')}</p>}
                                                        <h4>{t('booking.step3.policy8')}</h4>
                                                        <p>{t('booking.step3.policy9')}</p>
                                                        <p>{t('booking.step3.policy10')}<br/>
                                                            {t('booking.step3.policy11')}
                                                            <ul>
                                                                <li>{t('booking.step3.policy12')}</li>
                                                                <li>{t('booking.step3.policy13')}</li>
                                                            </ul>
                                                        </p>
                                                        {selectedExam.isDemo ?
                                                            <p>{t('booking.step3.policy14')}</p> :
                                                            <p>{t('booking.step3.policy15')}</p>}
                                                    </Col>
                                                    <Col>
                                                        <h4>{t('booking.step3.policy16')}</h4>
                                                        <p>{t('booking.step3.policy17')}</p>
                                                        <p>{t('booking.step3.policy18')}</p>
                                                        <p><strong><Trans ns="booking" i18nKey="booking.step3.policy.noAutomatedDecision" components={{strong: <strong/>}}/></strong></p>
                                                        <h4>{t('booking.step3.policy20')}</h4>
                                                        <p>{t('booking.step3.policy21')}</p>
                                                        <p>{t('booking.step3.policy22')}</p>
                                                        <p><Trans ns="booking" i18nKey="booking.step3.policy.noAiImageProcessing" components={{strong: <strong/>}}/></p>
                                                    </Col>
                                                </Row>
                                                <hr/>
                                                <Form>
                                                    <h3 className={"d-flex justify-content-center " + (policyChecked? "text-success" : "text-warning")}>
                                                        <Form.Check
                                                            type="checkbox"
                                                            id="custom-switch2"
                                                            label={t("booking.step3.policy24")}
                                                            checked={policyChecked}
                                                            onChange={(event) =>
                                                                setPolicyChecked(event.currentTarget.checked)
                                                            }
                                                        /></h3>
                                                </Form>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                                {!selectedExam.isDemo ? <Row className="mt-4">
                                        <Col>
                                            <Card>
                                                <Card.Header><h2>{t('booking.step3.tucan1')}</h2>
                                                </Card.Header>
                                                <Card.Body>
                                                    <ul>
                                                        <li>{t('booking.step3.tucan2')}</li>
                                                        <li>{t('booking.step3.tucan3')}<br/>
                                                            {t('booking.step3.tucan4a')}
                                                            {/*<a*/}
                                                            {/*    href="https://www.tu-darmstadt.de/studieren/studierende_tu/studienorganisation_und_tucan/hilfe_und_faq/artikel_details_de_en_14403.de.jsp"*/}
                                                            {/*    target="_blank">{t('booking.step3.tucan4b')}</a>*/}
                                                            {/*<b> {t('booking.step3.tucan4c')}</b> ({t('booking.step3.tucan4d')} {moment(selectedTimeSlot.startAt).format('DD.MM.YYYY')}) {t('booking.step3.tucan4e')}*/}
                                                        </li>
                                                    </ul>
                                                    <hr/>
                                                    <Form>
                                                        <h3 className={"d-flex justify-content-center " + (tucanNoteChecked? "text-success" : "text-warning")}>
                                                            <Form.Check
                                                                type="checkbox"
                                                                id="custom-switch3"
                                                                label={t("booking.step3.tucan5")}
                                                                checked={tucanNoteChecked}
                                                                onChange={(event) =>
                                                                    setTucanNoteChecked(
                                                                        event.currentTarget.checked
                                                                    )
                                                                }
                                                            /></h3>
                                                    </Form>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row> :
                                    <Alert variant="info" className="mt-4 d-flex align-items-start gap-2">
                                        <i className="bi bi-info-circle-fill fs-5" aria-hidden="true"/>
                                        <span><Trans ns="booking" i18nKey="booking.step3.demoNotice" components={{strong: <strong/>}}/></span>
                                    </Alert>}
                            </Card.Body>
                            <Row className="booking-actions g-2 mx-0">
                                <Col className="m-2">
                                    <Button variant="outline-secondary" onClick={() => setView(1)}><i
                                        className="bi bi-caret-left-square-fill"></i> {t('booking.step1.button.previous')}
                                    </Button>
                                </Col>
                                <Col className="m-2 d-flex justify-content-end">
                                    <Button variant="success" onClick={() => void confirmBooking()}
                                            disabled={!infoChecked || !policyChecked || (selectedExam.isDemo ? false : !tucanNoteChecked)}> {t('booking.step3.btn')} <i className="bi bi-caret-right-square-fill"></i>
                                    </Button>
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                </Row>) : null }
            </Container>
        </div>
    )
}

export default BookingComponent
